import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginService } from '../../core/services/login.service';
import {
    PnpUserService,
    PnpLoginService,
    IUserInvitationResponse,
    ILoginResponse,
    AuthErrors,
    AuthErrorService,
    IRegistrationResponse,
    AuthService,
    IUserResponse,
    IUserPhotoResponse,
    eInvitations,
} from '@pn-plus/pnp-services';
import { FingerprintService } from '../../core/services/fingerprint.service';
import { catchError, first, firstValueFrom, map, mergeMap, Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IAuthModel } from '../../../../../../libs/pnp-services/src/lib/auth/_models/auth.model';

@Component({
    selector: 'pn-plus-invitation',
    templateUrl: './invitation.component.html',
    styleUrls: ['./invitation.component.scss'],
})
export class InvitationComponent implements OnInit {
    activeError: any;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private pnpUserService: PnpUserService,
        private pnpLoginService: PnpLoginService,
        private fingerprintService: FingerprintService,
        private loginService: LoginService,
        private authService: AuthService,
        private authErrorService: AuthErrorService,
    ) {}

    async ngOnInit() {
        const uuidInvitation = this.route.snapshot.paramMap.get('uuid') || '';
        const invitationResponse: IUserInvitationResponse  = 
            await firstValueFrom(this.getInvitationAndToken(uuidInvitation)) as IUserInvitationResponse;

        if (invitationResponse?.token) {
            this.initAuthInvitationModel(invitationResponse);
        }
    }

    private getInvitationAndToken(uuidInvitation: string) {
        return this.pnpUserService.getInvitation(uuidInvitation)
        .pipe(
            map((responseInvitation: IUserInvitationResponse) => 
                responseInvitation,
            ),
            catchError((errorResponse: HttpErrorResponse) => {
                if (errorResponse.error.type === 'ExpiredInvitation') {
                    this.loginService.navigate('/invitation/expired');
                } else {
                    this.loginService.navigate('/invitation/not-found');
                }
                return of(null);
            }),
        );
    }

    private async initAuthInvitationModel(invitationResponse: IUserInvitationResponse ) {
        let authInvitationModel: IAuthModel;
        this.authService.resetLogin();
        authInvitationModel = {
            loginPayload: {
                deviceId: `${this.fingerprintService.getFingerPrint()}`,
                tokenId: invitationResponse.token,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            error: null,
            type: invitationResponse.type,
        };
        authInvitationModel = await firstValueFrom(this.tryLoginInvitation(authInvitationModel));
        const accessToken = authInvitationModel.loginResponse?.accessToken;

        if(accessToken) {
            authInvitationModel = await this.processRegistration(authInvitationModel);
        }

        if (authInvitationModel.error) {
            const activeError = this.authErrorService.getErrorByType(authInvitationModel.error.type);
            if(activeError === AuthErrors.UserLocked) {
                this.activeError = authInvitationModel.error;
            } else {
                this.activeError = activeError;
            }
        }
    }

    private tryLoginInvitation(authInvitationModel: IAuthModel): Observable<any> {
        return this.pnpLoginService.postLoginInvitation(authInvitationModel.loginPayload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authInvitationModel.error = errorResponse.error;
                    return of(null);
                }),
                map((loginResponse?: ILoginResponse | null) => {
                    if (loginResponse) {
                        authInvitationModel.loginResponse = loginResponse;
                    }
                    this.authService.updateAuthModel(authInvitationModel);
                    return authInvitationModel;
                }),
            );
    }

    private async processRegistration(authInvitationModel: IAuthModel) {
        authInvitationModel = await firstValueFrom(this.loadUserRegistration(authInvitationModel));
        authInvitationModel = await firstValueFrom(this.loadUser(authInvitationModel));
        authInvitationModel = await firstValueFrom(this.loadUserPhoto(authInvitationModel));
        authInvitationModel = await this.loginService.storeBase64Avatar(authInvitationModel);

        authInvitationModel.error = AuthErrors.Success;
        this.authService.updateAuthModel(authInvitationModel);

        this.loginService.setInvitationProcess(true);
        this.loginService.navigateRegistration(authInvitationModel);
        return authInvitationModel;
    }

    private loadUserRegistration(authInvitationModel: IAuthModel) {
        if (authInvitationModel.error) {
            return of(authInvitationModel);
        }

        return this.pnpUserService.getUserRegistration()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authInvitationModel.error = errorResponse.error;
                    return of(null);
                }),
                map((registrationResponse: IRegistrationResponse | null) => {
                    if(registrationResponse) {
                        if(authInvitationModel.type === eInvitations.ResetPassword) {
                            registrationResponse = {
                                hasPassword: false,
                                hasPin: false,
                                hasPhoneNumber: false,
                                hasPhoneNumberConfirmed: false,
                                hasCompletedRegistration: false,
                            };
                        }
                        authInvitationModel.registrationResponse = registrationResponse;
                    }
                    return authInvitationModel;
                }),
            );
    }

    private loadUser(authInvitationModel: IAuthModel) {
        if (authInvitationModel.error) {
            return of(authInvitationModel);
        }

        return this.pnpUserService.getUser()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authInvitationModel.error = errorResponse.error;
                    return of(null);
                }),
                map((userResponse: IUserResponse | null) => {
                    if(userResponse) {
                        authInvitationModel.userResponse = userResponse;
                    }
                    return authInvitationModel;
                }),
            );
    }

    private loadUserPhoto(authInvitationModel: IAuthModel) {
        if (authInvitationModel.error) {
            return of(authInvitationModel);
        }

        return this.pnpUserService.getUserPhoto()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    authInvitationModel.error = errorResponse.error;
                    return of(null);
                }),
                map((userPhotoResponse: IUserPhotoResponse | null) => {
                    if(userPhotoResponse){
                        authInvitationModel.userPhotoResponse = userPhotoResponse;
                    }
                    return authInvitationModel;
                }),
            );
    }
}
